import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowPathIcon, ExclamationTriangleIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatDateTime, formatTradingDay, formatMeterRaw } from './dailyDataUtils'

const TOTAL_ROWS = [
  { key: 'turnover', label: 'Turnover' },
  { key: 'cancelcredit', label: 'Cancel credits' },
  { key: 'cashin', label: 'Cash in' },
  { key: 'cashdrop', label: 'Cash drop' },
  { key: 'cashlessin', label: 'Cashless in' },
  { key: 'cashlessout', label: 'Cashless out' },
  { key: 'creditwin', label: 'Credit win' },
]

export default function MeterReconciliationPanel({
  tradingDay,
  onTradingDayChange,
  refreshKey,
  title = 'Reconciliation',
  showDatePicker = true,
  prefetchedReport = null,
  prefetchedLoading = false,
  skipOwnFetch = false,
}) {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showImportRuns, setShowImportRuns] = useState(false)

  const usePrefetch =
    skipOwnFetch ||
    (prefetchedReport != null && prefetchedReport.trading_day === tradingDay)

  const load = useCallback(async () => {
    if (!tradingDay || skipOwnFetch) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ trading_day: tradingDay })
      const res = await authFetch(getLocalApiUrl(`/api/daily-data/meter/reconciliation?${params}`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load reconciliation')
      setReport(json.data)
    } catch (e) {
      setError(e.message)
      setReport(null)
    } finally {
      setLoading(false)
    }
  }, [tradingDay, skipOwnFetch])

  useEffect(() => {
    if (skipOwnFetch) {
      setReport(prefetchedReport)
      setLoading(false)
      setError(null)
      return
    }
    if (prefetchedReport != null && prefetchedReport.trading_day === tradingDay) {
      setReport(prefetchedReport)
      setLoading(false)
      setError(null)
      return
    }
    load()
  }, [load, refreshKey, skipOwnFetch, prefetchedReport, tradingDay])

  const displayReport = skipOwnFetch
    ? prefetchedReport
    : (prefetchedReport?.trading_day === tradingDay ? prefetchedReport : report)
  const displayLoading = skipOwnFetch ? prefetchedLoading : loading
  const recon = displayReport?.reconciliation
  const completeness = recon?.completeness

  return (
    <div className="bg-white shadow rounded-lg p-6 border-l-4 border-indigo-500">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-600 mt-1">
            Trading-day roll-up from ingested meter readings (completeness, deltas, resets).
          </p>
          {displayReport?.label && (
            <p className="text-xs text-amber-700 mt-1">{displayReport.label}</p>
          )}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={displayLoading || !tradingDay || skipOwnFetch}
          className="inline-flex items-center px-3 py-2 border rounded-md text-sm hover:bg-gray-50 disabled:opacity-50"
          title={skipOwnFetch ? 'Use the page Refresh button to reload' : undefined}
        >
          <ArrowPathIcon className={`w-4 h-4 mr-1 ${displayLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {showDatePicker && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Trading day</label>
          <input
            type="date"
            value={tradingDay || ''}
            onChange={(e) => onTradingDayChange?.(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
          />
        </div>
      )}

      {error && !skipOwnFetch && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {displayLoading && !displayReport && (
        <p className="text-sm text-gray-500">Loading reconciliation…</p>
      )}

      {!displayLoading && displayReport && !recon?.completeness?.distinct_egms && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          No meter readings in the database for {formatTradingDay(tradingDay)}.
          {' '}
          <Link to="/monitor-safe/daily-data/meter" className="text-primary-600 hover:underline font-medium">
            Import meter data
          </Link>
          {' '}or choose another trading day above.
        </div>
      )}

      {recon && recon.completeness?.distinct_egms > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase">EGMs</div>
              <div className="text-lg font-semibold">{completeness?.distinct_egms ?? '—'}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Snapshots</div>
              <div className="text-lg font-semibold">{completeness?.total_snapshots ?? '—'}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Avg / EGM</div>
              <div className="text-lg font-semibold">{completeness?.avg_snapshots_per_egm ?? '—'}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500 uppercase">Imports today</div>
              <div className="text-lg font-semibold">{displayReport?.import_run_count ?? 0}</div>
            </div>
          </div>

          {(completeness?.snapshot_min || completeness?.snapshot_max) && (
            <p className="text-xs text-gray-500 mb-4">
              Snapshot coverage: {formatDateTime(completeness.snapshot_min)} — {formatDateTime(completeness.snapshot_max)}
              {displayReport?.last_import_at && (
                <> · Last import {formatDateTime(displayReport.last_import_at)}</>
              )}
            </p>
          )}

          {(recon.resets?.egm_count > 0) && (
            <div className="mb-4 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
              <div>
                <strong>{recon.resets.egm_count} EGM(s)</strong> with meter reset detected ({recon.resets.event_count} event(s)).
                Cross-check IGT Machine Meter Variance; Banking Summary may align after venue Accept.
              </div>
            </div>
          )}

          <h3 className="text-sm font-semibold text-gray-900 mb-2">Daily deltas (AUD)</h3>
          <table className="min-w-full divide-y divide-gray-200 mb-6">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">PaySafeGO total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {TOTAL_ROWS.map(({ key, label }) => (
                <tr key={key}>
                  <td className="px-3 py-2 text-sm text-gray-800">{label}</td>
                  <td className="px-3 py-2 text-sm text-right font-mono">{formatMeterRaw(recon.totals?.[key])}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {displayReport?.import_runs?.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <button
                type="button"
                onClick={() => setShowImportRuns(!showImportRuns)}
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <ChevronDownIcon
                  className={`w-4 h-4 transition-transform ${showImportRuns ? 'rotate-180' : ''}`}
                />
                Import runs ({displayReport.import_runs.length} file{displayReport.import_runs.length !== 1 ? 's' : ''})
              </button>
              {showImportRuns && (
                <ul className="mt-2 text-sm text-gray-700 space-y-1 ml-6">
                  {displayReport.import_runs.map((run) => (
                    <li key={run.id} className="font-mono text-xs">
                      {formatDateTime(run.run_at)} — {run.status} — imported {run.imported}
                      {run.file_path ? ` — ${run.file_path}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-4">
            Totals are summed from ingested meter counter deltas (stored in cents, shown as AUD). Compare with venue Banking Summary in your club&apos;s separate reporting process.
          </p>

          {recon?.slot_analysis !== undefined && recon?.slot_analysis !== null && (
            <>
              <h3 className="text-sm font-semibold text-gray-900 mt-6 mb-3">
                Slot Analysis Summary (from FactSlotAnalysisDaily)
              </h3>
              
              <div className="mb-3 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded p-2">
                <strong>Jackpot Types:</strong> Progressive = linked jackpots across machines | 
                Jackpot Win = total jackpot payouts | Handpays = manual payouts by staff
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg bg-purple-50 border border-purple-100 p-3">
                  <div className="text-xs text-purple-600 uppercase font-medium">Cancel Credit</div>
                  <div className="text-xl font-bold text-purple-900">
                    {formatMeterRaw(recon.slot_analysis.total_cancel_credit)}
                  </div>
                  <div className="text-xs text-purple-600 mt-1">
                    {recon.slot_analysis.egms_with_cancel_credit} EGMs
                  </div>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
                  <div className="text-xs text-amber-600 uppercase font-medium">Jackpot Wins (Total)</div>
                  <div className="text-xl font-bold text-amber-900">
                    {formatMeterRaw(recon.slot_analysis.total_jackpot)}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">
                    {recon.slot_analysis.egms_with_jackpots} EGMs won
                  </div>
                </div>
                <div className="rounded-lg bg-orange-50 border border-orange-100 p-3">
                  <div className="text-xs text-orange-600 uppercase font-medium">Progressive</div>
                  <div className="text-xl font-bold text-orange-900">
                    {formatMeterRaw(recon.slot_analysis.total_progressive)}
                  </div>
                  <div className="text-xs text-orange-600 mt-1">Linked jackpots</div>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                  <div className="text-xs text-blue-600 uppercase font-medium">Handpays</div>
                  <div className="text-xl font-bold text-blue-900">
                    {recon.slot_analysis.total_handpays || 0}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">Manual payouts</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Turnover (Slot Analysis)</div>
                  <div className="text-lg font-semibold">{formatMeterRaw(recon.slot_analysis.total_turnover)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Net Win</div>
                  <div className="text-lg font-semibold">{formatMeterRaw(recon.slot_analysis.total_net_win)}</div>
                </div>
                <div className="rounded-lg bg-purple-50 border border-purple-100 p-3">
                  <div className="text-xs text-purple-600 uppercase font-medium">Machine Payouts</div>
                  <div className="text-lg font-bold text-purple-900">
                    {formatMeterRaw(recon.slot_analysis.machine_payouts || 0)}
                  </div>
                  <div className="text-xs text-purple-600 mt-1">
                    Total Processed - Machine Retained
                  </div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">EGMs in File</div>
                  <div className="text-lg font-semibold">{recon.slot_analysis.egm_count}</div>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Slot analysis data from FactSlotAnalysisDaily file (daily EGM-level summary). 
                Values shown in AUD (converted from cents). 
                <span className="font-medium text-purple-700">
                  {' '}• Machine Payouts calculated from slot data (may not include manual cage payouts or promotional vouchers)
                </span>
                {recon.slot_analysis.egms_with_jackpots > 0 && (
                  <span className="font-medium text-amber-700">
                    {' '}• {recon.slot_analysis.egms_with_jackpots} machines had jackpot wins today
                  </span>
                )}
              </p>
            </>
          )}

          {recon?.sessions !== undefined && recon?.sessions !== null && (
            <>
              <h3 className="text-sm font-semibold text-gray-900 mt-6 mb-3">
                Gaming Sessions Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3">
                  <div className="text-xs text-indigo-600 uppercase font-medium">Total Sessions</div>
                  <div className="text-xl font-bold text-indigo-900">
                    {recon.sessions.total_sessions.toLocaleString()}
                  </div>
                  <div className="text-xs text-indigo-600 mt-1">
                    {recon.sessions.carded_percentage}% carded
                  </div>
                </div>
                <div className="rounded-lg bg-green-50 border border-green-100 p-3">
                  <div className="text-xs text-green-600 uppercase font-medium">Unique Players</div>
                  <div className="text-xl font-bold text-green-900">
                    {recon.sessions.unique_players.toLocaleString()}
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {recon.sessions.unique_egms} EGMs
                  </div>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                  <div className="text-xs text-blue-600 uppercase font-medium">Turnover</div>
                  <div className="text-xl font-bold text-blue-900">
                    {formatMeterRaw(recon.sessions.total_turnover)}
                  </div>
                  <div className="text-xs text-blue-600 mt-1">From sessions</div>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3">
                  <div className="text-xs text-amber-600 uppercase font-medium">Games Played</div>
                  <div className="text-xl font-bold text-amber-900">
                    {recon.sessions.total_games.toLocaleString()}
                  </div>
                  <div className="text-xs text-amber-600 mt-1">Total spins</div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Cash In</div>
                  <div className="text-lg font-semibold">{formatMeterRaw(recon.sessions.total_cashin)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Cash Out</div>
                  <div className="text-lg font-semibold">{formatMeterRaw(recon.sessions.total_cashout)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Credit Win</div>
                  <div className="text-lg font-semibold">{formatMeterRaw(recon.sessions.total_creditwin)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-xs text-gray-500 uppercase">Carded vs Uncarded</div>
                  <div className="text-lg font-semibold">
                    {recon.sessions.carded_sessions} / {recon.sessions.uncarded_sessions}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Gaming sessions data from FactPlayerSession file (individual play sessions). 
                Values shown in AUD (converted from cents).
              </p>
            </>
          )}
        </>
      )}
    </div>
  )
}
